import React, { useState, useRef, useEffect } from "react";
import { Search, Check, Plus } from "lucide-react";
import { PROFESSIONS } from "@/lib/professions";
import { cn } from "@/lib/utils";

// Searchable profession selector with "add custom profession". Custom values are kept
// on the user only — never added to the global PROFESSIONS list.
export function ProfessionSelect({ value, onChange, testid = "profession-select" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = PROFESSIONS.filter((p) => p.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 40);
  const custom = q.trim() && !PROFESSIONS.some((p) => p.toLowerCase() === q.trim().toLowerCase());
  const pick = (v) => { onChange(v); setOpen(false); setQ(""); };

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testid} className="flex w-full items-center justify-between rounded-xl border border-am-border bg-am-surface2 px-3 py-2.5 text-left text-sm text-white outline-none focus:border-am-border-strong">
        <span className={value ? "text-white" : "text-am-text3"}>{value || "Select or type a profession"}</span>
        <Search className="h-4 w-4 text-am-text3" />
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-full overflow-hidden rounded-xl border border-am-border-strong bg-am-surface2 shadow-2xl">
          <div className="flex items-center gap-2 border-b border-am-border px-3">
            <Search className="h-4 w-4 text-am-text3" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} placeholder="Search professions…" className="w-full bg-transparent py-2.5 text-sm text-white outline-none" />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {custom && (
              <button type="button" onClick={() => pick(q.trim())} data-testid={`${testid}-custom`} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-white hover:bg-white/5">
                <Plus className="h-4 w-4 aura-text" /> Add "{q.trim()}"
              </button>
            )}
            {filtered.map((p) => (
              <button key={p} type="button" onClick={() => pick(p)} className="flex w-full items-center justify-between px-3 py-2 text-sm text-am-text hover:bg-white/5">
                {p} {value === p && <Check className="h-4 w-4 text-aura-mint" />}
              </button>
            ))}
            {filtered.length === 0 && !custom && <p className="px-3 py-3 text-xs text-am-text3">No matches.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
